import * as React from 'react'
import { ArrowRight, HelpCircle } from 'lucide-react'

import type { LeadQualificationFunnel as LeadQualificationFunnelData } from '@sdr-agend/admin-schemas'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

/**
 * "Funil de Qualificação (Lead Novo)" — 2026-07-20 (user request).
 *
 * Distinct from the rest of the dashboard: `confirmation` campaigns handle
 * confirmação/reagendamento of meetings ALREADY booked, while `lead_new`
 * campaigns exist for ONE job — qualify a fresh lead and generate a meeting
 * for future sales/upgrade. This block answers "how is lead_new doing at
 * that job" directly, instead of requiring a manual DB investigation every
 * time someone asks.
 *
 * Visual (Story 10.6, 2026-07-30, user request — redesign): caixas
 * conectadas por setas, uma por etapa (estilo referência do usuário),
 * substituindo as barras empilhadas da v1. Ainda plain HTML/CSS — same
 * convention as `AttendanceChart.tsx` (no chart library in this codebase).
 * Cada caixa depois da primeira mostra a taxa de conversão da etapa
 * ANTERIOR, não do topo — esse é o número que o operador realmente usa
 * ("dos qualificados, quantos agendaram").
 *
 * QA fix (2026-08-05, user request — produção real: julho/2026 mostrava
 * "Agendados: 381" MAIOR que "Leads Recebidos: 305", com "778% da etapa
 * anterior") — `scheduled`/`attended`/`noShow` (2026-08-05) passaram a
 * contar QUALQUER reunião com conversa de confirmação vinculada,
 * independente de ter vindo de um lead novo deste mês (ver
 * `queryLeadQualificationFunnel` em `dashboard.ts`). Isso quebrou a
 * premissa do funil encadeado — essas 3 caixas deixaram de ser um
 * subconjunto de "Leads Recebidos"/"Qualificados". Por isso NÃO fazem mais
 * parte do funil de setas: viraram cards independentes ("Confirmadas pela
 * IA"), sem seta de conversão da etapa anterior — mesmo espírito de como
 * "Vendas" já se comportava (nunca foi um subconjunto real do funil
 * lead_new, só nunca tinha ficado tão óbvio).
 */
export interface LeadQualificationFunnelProps {
  data: LeadQualificationFunnelData
  /**
   * Story 10.7 (2026-07-30, user request, AC2) — vendas do período
   * (`salesIndicator.count`). Opcional e por fora de
   * `LeadQualificationFunnelData` de propósito: essa venda vem do pipeline do
   * GHL (Story 10.5), não do funil de qualificação em si. Renderizado junto
   * com Agendados/Compareceram/No-show no card "Confirmadas pela IA" —
   * nenhum dos quatro é um subconjunto de "Leads Recebidos".
   */
  salesCount?: number
  /**
   * Redesign visual (2026-07-31, user request) — a referência separa o
   * "Funil Comercial" (só as caixas) da comparação por campanha em um card
   * dedicado (`CampaignComparisonTable`, orientação transposta). `false`
   * omite os tempos médios + a tabela "Por campanha" pra evitar mostrar o
   * mesmo `byCampaign` duas vezes na mesma página. Default `true` — mantém
   * o comportamento existente em qualquer outro call-site.
   */
  showDetails?: boolean
}

interface Stage {
  key: string
  label: string
  value: number
  borderColor: string
  iconColor: string
  /** 2026-08-11 (user request) — ver mesmo campo em `AiPerformancePanel.tsx`. */
  tooltip?: string
}

// QA fix (2026-08-05) — só as 3 etapas que realmente são um subconjunto
// encadeado de "Leads Recebidos" (todas escopadas à campanha Lead Novo do
// período). Agendados/Compareceram/No-show/Vendas saíram daqui — ver o
// bloco "Confirmadas pela IA" no JSX abaixo.
function buildStages(data: LeadQualificationFunnelData): Stage[] {
  return [
    {
      key: 'received',
      label: 'Leads Recebidos',
      value: data.leadsReceived,
      borderColor: 'border-t-blue-500',
      iconColor: 'text-blue-500',
    },
    {
      key: 'responded',
      label: 'Responderam',
      value: data.responded,
      borderColor: 'border-t-sky-500',
      iconColor: 'text-sky-500',
    },
    {
      key: 'qualified',
      label: 'Qualificados',
      value: data.qualified,
      borderColor: 'border-t-violet-500',
      iconColor: 'text-violet-500',
      tooltip:
        'Sobre quem respondeu, não sobre o total de leads recebidos. A "Taxa de qualificação" em Performance da IA usa uma base diferente (todos os leads recebidos) — por isso os dois números não batem.',
    },
  ]
}

/** Exportado pra `AiPerformancePanel` reaproveitar (mesmos 3 tempos médios). */
export function formatMinutes(minutes: number | null): string {
  if (minutes === null) return 'N/A'
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = minutes / 60
  if (hours < 24) return `${hours.toFixed(1)}h`
  const days = hours / 24
  return `${days.toFixed(1)}d`
}

function formatConversionRate(current: number, previous: number): string | null {
  if (previous === 0) return null
  return `${Math.round((current / previous) * 100)}%`
}

/**
 * QA fix (2026-08-04, user request) — "Compareceram" usava
 * `attended / scheduled` (etapa anterior), mas `scheduled` inclui QUALQUER
 * agendamento no período, mesmo os que ainda nem aconteceram (futuros/
 * pendentes) — inflando o denominador e mostrando uma taxa artificialmente
 * baixa em períodos recentes/curtos. A taxa de comparecimento de verdade só
 * faz sentido sobre reuniões JÁ FINALIZADAS (`attended + noShow`), mesma
 * fórmula do `attendanceRate` do topo do dashboard (`ratio()` em
 * `dashboard.ts`).
 */
function formatAttendanceRate(attended: number, noShow: number): string | null {
  const denom = attended + noShow
  if (denom === 0) return null
  return `${Math.round((attended / denom) * 100)}%`
}

export function LeadQualificationFunnel({
  data,
  salesCount,
  showDetails = true,
}: LeadQualificationFunnelProps): React.ReactElement {
  const stages = buildStages(data)

  if (data.leadsReceived === 0) {
    return (
      <div
        className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground"
        data-testid="lead-funnel-empty"
      >
        Nenhum lead recebido pelas campanhas de Lead Novo no período selecionado.
      </div>
    )
  }

  return (
    <div data-testid="lead-qualification-funnel">
      {/* QA fix (2026-07-31, user request) — as caixas crescem pra dividir o
          espaço disponível (`flex-1`), com um piso de `min-w-[92px]` — abaixo
          disso, "QUALIFICADOS"/"RESPONDERAM" (palavras sem espaço, não
          quebram linha) forçavam a caixa a estourar a própria largura. O
          `overflow-x-auto` é só a rede de segurança pra viewports bem
          estreitos (mobile) — a caixa "Funil Comercial" em `Dashboard.tsx`
          já ocupa a linha inteira pra isso nunca precisar entrar em ação. */}
      <div className="overflow-x-auto">
        <div
          className="flex items-stretch gap-2"
          data-testid="lead-qualification-funnel-stages"
        >
          {stages.map((stage, i) => {
            const previous = i > 0 ? stages[i - 1] : undefined
            const conversionFromPrevious =
              previous !== undefined ? formatConversionRate(stage.value, previous.value) : null

            return (
              <React.Fragment key={stage.key}>
                <div
                  className={cn(
                    'flex min-w-[92px] flex-1 flex-col items-center gap-1 rounded-lg border border-t-2 bg-card px-3 py-3 text-center',
                    stage.borderColor,
                  )}
                  data-testid={`funnel-stage-${stage.key}`}
                >
                  <span className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {stage.label}
                    {stage.tooltip !== undefined && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="shrink-0 normal-case text-muted-foreground/70 hover:text-foreground"
                            aria-label={`O que é "${stage.label}"?`}
                          >
                            <HelpCircle className="h-3 w-3" aria-hidden="true" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent>{stage.tooltip}</TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                  <span className="text-2xl font-semibold tabular-nums">
                    {stage.value.toLocaleString('pt-BR')}
                  </span>
                  {previous !== undefined && (
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {conversionFromPrevious ?? '—'} da etapa anterior
                    </span>
                  )}
                </div>
                {i < stages.length - 1 && (
                  <div
                    className={cn('flex shrink-0 items-center justify-center', stage.iconColor)}
                    aria-hidden="true"
                  >
                    <ArrowRight className="h-5 w-5" />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>

      {/* QA fix (2026-08-05, user request) — Agendados/Compareceram/No-show/
          Vendas NÃO são mais caixas do funil encadeado (ver docstring do
          componente): contam qualquer reunião confirmada pela IA,
          independente de ter vindo de um lead novo DESTE período, então não
          são um subconjunto de "Leads Recebidos". Cards sem seta de
          conversão da etapa anterior — só "Compareceram" ganha sua própria
          taxa (comparecidos / (comparecidos + no-show)). */}
      <div className="mt-4">
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Confirmadas pela IA — todas as origens, não só Lead Novo
        </p>
        <div
          className="grid grid-cols-2 gap-3 sm:grid-cols-4"
          data-testid="lead-funnel-confirmed-by-ai"
        >
          <div className="rounded-md border border-t-2 border-t-amber-500 p-2.5" data-testid="funnel-confirmed-scheduled">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Agendados</p>
            <p className="text-lg font-semibold tabular-nums">{data.scheduled.toLocaleString('pt-BR')}</p>
          </div>
          <div className="rounded-md border border-t-2 border-t-emerald-500 p-2.5" data-testid="funnel-confirmed-attended">
            <p className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              Compareceram
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 normal-case text-muted-foreground/70 hover:text-foreground"
                    aria-label='O que é "Compareceram"?'
                  >
                    <HelpCircle className="h-3 w-3" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  Reuniões que passaram pelo fluxo de confirmação da IA (C1/C2/C3), de qualquer
                  origem. Diferente do "Comparecimento" no topo do dashboard, que conta qualquer
                  reunião finalizada no período, mesmo sem confirmação da IA.
                </TooltipContent>
              </Tooltip>
            </p>
            <p className="text-lg font-semibold tabular-nums">{data.attended.toLocaleString('pt-BR')}</p>
            <p className="text-[11px] tabular-nums text-muted-foreground">
              {formatAttendanceRate(data.attended, data.noShow) ?? '—'} de comparecimento
            </p>
          </div>
          <div className="rounded-md border border-t-2 border-t-red-500 p-2.5" data-testid="funnel-confirmed-noshow">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">No-show</p>
            <p className="text-lg font-semibold tabular-nums">{data.noShow.toLocaleString('pt-BR')}</p>
          </div>
          {salesCount !== undefined && (
            <div className="rounded-md border border-t-2 border-t-fuchsia-500 p-2.5" data-testid="funnel-confirmed-sales">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Vendas</p>
              <p className="text-lg font-semibold tabular-nums">{salesCount.toLocaleString('pt-BR')}</p>
            </div>
          )}
        </div>
      </div>

      {showDetails && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-md border p-2.5">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Tempo até 1ª resposta
              </p>
              <p className="text-lg font-semibold tabular-nums">
                {formatMinutes(data.avgMinutesToFirstResponse)}
              </p>
            </div>
            <div className="rounded-md border p-2.5">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Tempo até qualificação
              </p>
              <p className="text-lg font-semibold tabular-nums">
                {formatMinutes(data.avgMinutesToQualified)}
              </p>
            </div>
            <div className="rounded-md border p-2.5">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Tempo até agendamento
              </p>
              <p className="text-lg font-semibold tabular-nums">
                {formatMinutes(data.avgMinutesToScheduled)}
              </p>
            </div>
          </div>

          {data.byCampaign.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Por campanha
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-1.5 pr-3 font-medium">Campanha</th>
                      <th className="pb-1.5 pr-3 text-right font-medium">Recebidos</th>
                      <th className="pb-1.5 pr-3 text-right font-medium">Qualificados</th>
                      <th className="pb-1.5 text-right font-medium">Agendados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byCampaign.map((row) => (
                      <tr key={row.campaignId} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 font-medium">{row.campaignName}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {row.leadsReceived.toLocaleString('pt-BR')}
                        </td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {row.qualified.toLocaleString('pt-BR')}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {row.scheduled.toLocaleString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default LeadQualificationFunnel
